// ============================================================
// Stocora — SetPasswordPage (new, 2026-07-19)
// ============================================================
// Landing page for BOTH flows that authenticate a user via a
// one-time link before they have a usable password:
//
//   1. A brand-new invite (invite-clinic-member Edge Function →
//      Supabase's inviteUserByEmail → this page).
//   2. A password reset (LoginPage "Forgot password?" →
//      resetPasswordForEmail → this page).
//
// Both land here via the same route: AuthCallbackPage exchanges
// the link's code for a session, then always redirects to
// /set-password (carrying `?next=`) rather than the final
// destination directly — see AuthCallbackPage for why the
// distinction between invite/recovery doesn't need to be parsed
// out of the URL: either way, the very next required step is
// "set a password," so one page serves both.
//
// Requires an active session (established by AuthCallbackPage's
// code exchange) but NOT clinic resolution — a brand-new invitee
// already has their clinic_members row (invite-clinic-member wrote
// it atomically with the auth.users row), but a user who is mid-
// flow shouldn't be routed through AuthenticatedLayout's clinic
// state machine just to type a password. Lives under PublicLayout;
// does its own lightweight session check.
// ============================================================

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { safeNextPath } from '@/lib/auth';
import Icon from '@/components/Icon';
import StocoraLogo from '@/components/StocoraLogo';

const MIN_PASSWORD_LENGTH = 8;

interface SetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

type PageState =
  | { kind: 'checking_session' }
  | { kind: 'no_session' }
  | { kind: 'ready' }
  | { kind: 'saving' }
  | { kind: 'saved' };

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ kind: 'checking_session' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SetPasswordFormValues>({
    mode: 'onBlur',
    defaultValues: { password: '', confirmPassword: '' },
  });

  // AuthCallbackPage's code exchange should have already produced a
  // session by the time this mounts (it navigates here only on
  // success), but check directly rather than trust that ordering
  // blindly — a user could land here with a stale/bookmarked URL.
  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data, error }) => {
      if (cancelled) return;
      if (error || !data.session) {
        setState({ kind: 'no_session' });
        return;
      }
      setState({ kind: 'ready' });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    setErrorMessage(null);
    setState({ kind: 'saving' });

    try {
      const { error } = await supabase.auth.updateUser({ password: data.password });

      if (error) {
        setErrorMessage(describeUpdateError(error.message));
        setState({ kind: 'ready' });
        return;
      }

      setState({ kind: 'saved' });
      window.setTimeout(() => {
        navigate(safeNextPath(searchParams.get('next')), { replace: true });
      }, 1200);
    } catch (err) {
      setErrorMessage('Network error. Check your connection and try again.');
      setState({ kind: 'ready' });
      console.error('updateUser threw:', err);
    }
  });

  // ---- Render -------------------------------------------------------

  if (state.kind === 'checking_session') {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-full text-center">
        <Spinner />
        <p className="text-sm text-slate-500 mt-3">One moment…</p>
      </div>
    );
  }

  if (state.kind === 'no_session') {
    return (
      <div className="p-6 flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
          <Icon name="x" className="w-7 h-7 text-red-500" strokeWidth={2} />
        </div>
        <h1 className="text-lg font-semibold text-slate-900">Link expired</h1>
        <p className="text-sm text-slate-600 mt-1.5 max-w-[280px]">
          This link is no longer valid. Request a new invite or password reset.
        </p>
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="mt-6 w-full h-11 rounded-xl bg-action hover:bg-action-hover active:bg-action-active transition text-sm font-semibold text-white"
        >
          Back to sign in
        </button>
      </div>
    );
  }

  if (state.kind === 'saved') {
    return (
      <div className="p-6 flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-2xl bg-success-surface flex items-center justify-center mb-4">
          <Icon name="check" className="w-7 h-7 text-success" strokeWidth={2} />
        </div>
        <h1 className="text-lg font-semibold text-slate-900">Password set</h1>
        <p className="text-sm text-slate-600 mt-1.5">Taking you in…</p>
      </div>
    );
  }

  const isSaving = state.kind === 'saving';
  const password = watch('password');

  return (
    <div className="p-6 flex flex-col min-h-full">
      <div className="flex flex-col items-center text-center pt-4">
        <div className="mb-5">
          <StocoraLogo size={32} />
        </div>
        <h1 className="text-lg font-semibold text-slate-900">Set your password</h1>
        <p className="text-sm text-slate-600 mt-1.5">
          You&rsquo;ll use this to sign in from now on.
        </p>
      </div>

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-3" noValidate>
        <div>
          <label
            htmlFor="new-password"
            className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5"
          >
            New password
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            disabled={isSaving}
            placeholder="••••••••"
            className={`w-full h-11 px-3.5 rounded-xl border text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-action/20 transition ${
              errors.password
                ? 'border-red-300 focus:border-red-500'
                : 'border-slate-200 focus:border-action'
            } ${isSaving ? 'bg-slate-50' : 'bg-white'}`}
            {...register('password', {
              required: 'Password is required',
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: `Must be at least ${MIN_PASSWORD_LENGTH} characters`,
              },
            })}
          />
          {errors.password && (
            <p className="text-xs text-red-600 mt-1.5">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="confirm-password"
            className="block text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5"
          >
            Confirm password
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            disabled={isSaving}
            placeholder="••••••••"
            className={`w-full h-11 px-3.5 rounded-xl border text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-action/20 transition ${
              errors.confirmPassword
                ? 'border-red-300 focus:border-red-500'
                : 'border-slate-200 focus:border-action'
            } ${isSaving ? 'bg-slate-50' : 'bg-white'}`}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (value) => value === password || 'Passwords don\u2019t match',
            })}
          />
          {errors.confirmPassword && (
            <p className="text-xs text-red-600 mt-1.5">{errors.confirmPassword.message}</p>
          )}
        </div>

        {errorMessage && (
          <div className="rounded-xl bg-red-50 border border-red-100 px-3 py-2.5">
            <p className="text-xs text-red-700 leading-relaxed">{errorMessage}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSaving}
          className="w-full h-11 mt-1 rounded-xl bg-action hover:bg-action-hover active:bg-action-active disabled:opacity-60 transition text-sm font-semibold text-white flex items-center justify-center gap-2"
        >
          {isSaving ? (
            <>
              <Spinner />
              Saving…
            </>
          ) : (
            'Set password'
          )}
        </button>
      </form>
    </div>
  );
}

// ---- helpers ------------------------------------------------------------

function describeUpdateError(message: string): string {
  const msg = message.toLowerCase();
  if (msg.includes('password') && msg.includes('least')) {
    return message; // Supabase's own length/strength message is already clear.
  }
  if (msg.includes('network') || msg.includes('failed to fetch')) {
    return 'Network error. Check your connection and try again.';
  }
  return "Couldn't set your password. Try again, or request a fresh link.";
}

function Spinner() {
  return (
    <span
      className="w-6 h-6 border-2 border-slate-200 border-t-action rounded-full animate-spin"
      aria-hidden="true"
    />
  );
}
